import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PaymentLine from '../PaymentLine';
import styles from './PayManually.scss';
import { CheckoutTypes, Modals } from '../../utils/constants';
import { getDisplayableSpreadDishesByPaymentIndex } from '../../utils/PayPerDishUtils';
import { createI18nObject } from '../../utils/i18nUtils';
import _ from 'lodash';
import { translate } from 'react-i18next';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';
import PaymentMethodSelection from '../PaymentMethodSelection';
import CashierWidget from '../CashierWidget';
import { isOfflinePayment } from '../../utils/CashierUtils';
import { TextButton, Loader } from 'wix-style-react';

const RESTAURANTS_PAYMENT_TYPES = ['cash', 'credit', 'il.co.mysodexo'];

function handleRestaurantsPaymentSelection({ type, addPayment, offlinePayments, collectionMethod, displayModal }) {
  switch (type) {
    case 'cash':
      addPayment({ paymentValue: { type: 'cash' } });
      break;
    case 'credit':
      if (offlinePayments) {
        addPayment({ paymentValue: { type: 'credit', collectionMethod: collectionMethod } });
      } else {
        displayModal({ modal: Modals.CREDIT_CARD_IFRAME });
      }
      break;
    case 'il.co.mysodexo':
      console.log('Need to implement');
      break;
    default:
      throw new Error(`Flow ${type} is not recognize`);
  }
}

function PayManually(props) {
  const [showPaymentSelection, setShowPaymentSelection] = useState(false);
  const havePayments = props.paymentsValue.length > 0;

  const onSelectPayment = type => {
    props.selectPaymentType(type);

    if (RESTAURANTS_PAYMENT_TYPES.includes(type)) {
      handleRestaurantsPaymentSelection({ ...props, type });
    } else {
      props.selectSinglePaymentMethod({ singlePaymentMethod: type });

      if (type === 'offline') {
        props.addCashierPayment({ paymentMethod: 'offline' });
        props.setPendingPayment({ hasPendingPayment: true });
      } else {
        props.displayModal({ modal: Modals.CASHIER_WIDGET_MODAL });
      }
    }

    setShowPaymentSelection(false);
  };

  const onRemovePaymentLine = (paymentIndex, payment) => {
    props.removePayment({ paymentIndex, payment });
  };

  const onPaymentLineChange = (paymentIndex, paymentValue) => {
    props.updatePayment({ paymentIndex, paymentValue });
  };

  const getContent = () => {
    const {
      allowSplitPayments,
      paymentTypes = [],
      paymentMethods,
      paymentMetadata,
      paymentsValue,
      locale,
      currency,
      checkoutType,
      setAssociateDishPaymentIndex,
      orderItems,
      payments,
      itemsHash,
      paymentAssociations,
      derivedPpdData,
      displayModal,
      acceptsTip,
      t,
      networks,
      hasPendingPayment,
    } = props;
    const haveOnePayment = paymentsValue.length === 1;
    const haveOnlyOfflinePayment = haveOnePayment && isOfflinePayment(paymentsValue[0]);
    const currencySymbol = CurrencyUtils.currencySymbol(currency);

    return (
      <div>
        {paymentsValue.map((paymentValue, paymentIndex) => {
          const displayableSpreadDishesByPaymentIndex = getDisplayableSpreadDishesByPaymentIndex({
            orderItems,
            payments,
            itemsHash,
            currency,
            i18n: createI18nObject(t),
            locale,
            ppd: paymentAssociations,
            paymentIndex,
          });

          const priceComponents = _.get(derivedPpdData[paymentIndex], 'priceComponents');
          const displayableDiscounts = _.get(derivedPpdData[paymentIndex], 'displayableDiscounts');

          return (
            <PaymentLine
              currencySymbol={currencySymbol}
              acceptsTip={acceptsTip}
              onRemove={() => onRemovePaymentLine(paymentIndex, payments[paymentIndex])}
              onChange={value => onPaymentLineChange(paymentIndex, value)}
              dataHook={`payment-line-wrapper-${paymentValue.type}`}
              key={`${paymentValue.type}-${paymentIndex}`}
              paymentValue={paymentValue}
              locale={locale}
              currency={currency}
              payPerDish={checkoutType === CheckoutTypes.PAY_PER_DISH}
              displayAmountAsLabel={haveOnePayment || checkoutType === CheckoutTypes.PAY_PER_DISH}
              onClick={() => {
                setAssociateDishPaymentIndex({ associateDishPaymentIndex: paymentIndex });
                displayModal({ modal: Modals.ASSOCIATE_DISH_MODAL });
              }}
              orderItems={displayableSpreadDishesByPaymentIndex}
              priceComponents={priceComponents}
              displayableDiscounts={displayableDiscounts}
              networks={networks}
              paymentMethods={paymentMethods}
            />
          );
        })}

        {(hasPendingPayment || haveOnlyOfflinePayment) && (
          <div className={styles.cashier}>
            <CashierWidget />
          </div>
        )}

        {showPaymentSelection || !havePayments ? (
          <PaymentMethodSelection
            paymentTypes={paymentTypes}
            paymentMethods={paymentMethods}
            onSelectPayment={onSelectPayment}
            paymentMetadata={paymentMetadata}
          />
        ) : !allowSplitPayments ? null : (
          <TextButton
            className={styles.splitPayment}
            dataHook="payment-split-payment-button"
            onClick={() => setShowPaymentSelection(true)}
          >
            {t('phoneordering_checkout_split_cta')}
          </TextButton>
        )}
      </div>
    );
  };

  const getLoader = () => {
    if (props.hasPendingPayment) {
      return (
        <div className={styles.loader} data-hook="pay-manually-loader">
          <Loader size="small" />
        </div>
      );
    } else {
      return null;
    }
  };

  return (
    <div className={styles.wrapper}>
      {getContent()}
      {getLoader()}
    </div>
  );
}

PayManually.dispalyName = 'PayManually';

PayManually.propTypes = {
  selectPaymentType: PropTypes.func,
  removePayment: PropTypes.func,
  addPayment: PropTypes.func,
  updatePayment: PropTypes.func,
  paymentsValue: PropTypes.array,
  paymentTypes: PropTypes.array,
  paymentMethods: PropTypes.array, // from cashier
  paymentMetadata: PropTypes.object, // from cashier
  allowSplitPayments: PropTypes.bool,
  currency: PropTypes.string,
  currencySymbol: PropTypes.string,
  locale: PropTypes.string,
  checkoutType: PropTypes.oneOf([CheckoutTypes.MANUALLY, CheckoutTypes.PAY_PER_DISH]),
  setAssociateDishPaymentIndex: PropTypes.func,
  orderItems: PropTypes.array,
  payments: PropTypes.array,
  itemsHash: PropTypes.object,
  paymentAssociations: PropTypes.array,
  derivedPpdData: PropTypes.array,
  displayModal: PropTypes.func,
  t: PropTypes.func,
  acceptsTip: PropTypes.bool,
  offlinePayments: PropTypes.bool,
  networks: PropTypes.array,
  collectionMethod: PropTypes.oneOf(['online', 'cod', 'phone']).isRequired,
  selectSinglePaymentMethod: PropTypes.func,
  addCashierPayment: PropTypes.func,
  setPendingPayment: PropTypes.func,
  hasPendingPayment: PropTypes.bool,
};

PayManually.defaultProps = {
  paymentsValue: [],
  orderItems: [],
  payments: [],
  itemsHash: {},
  paymentAssociations: [],
  derivedPpdData: [],
  networks: [],
  paymentMethods: [],
};

export default translate()(PayManually);
